/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useCallback } from 'react';
import PropTypes from "prop-types";
import { css } from '@emotion/react';
import classNames from 'classnames';
import { useGeneratedHtmlId } from '../../../services/accessibility';
import { EuiIcon } from '../../icon';
export var EuiSwitch = ({
  label,
  id,
  checked,
  disabled,
  compressed,
  onChange,
  className,
  css: customCss,
  showLabel = true,
  type = 'button',
  labelProps,
  ...rest
}) => {
  const switchId = useGeneratedHtmlId({
    conditionalId: id
  });
  const labelId = useGeneratedHtmlId({
    conditionalId: labelProps?.id
  });
  const onClick = useCallback(e => {
    if (disabled) {
      return;
    }
    const event = e;
    event.target.checked = !checked;
    onChange(event);
  }, [checked, disabled, onChange]);
  const classes = classNames('euiSwitch', {
    'euiSwitch--compressed': compressed
  }, className);
  const labelClasses = classNames('euiSwitch__label', labelProps?.className);
  if (showLabel === false && typeof label !== 'string') {
    console.warn('EuiSwitch `label` must be a string when `showLabel` is false.');
  }
  const styles = {
    euiSwitch: /*#__PURE__*/css(";label:euiSwitch;")
  }; // TODO: Emotion conversion
  const cssStyles = [styles.euiSwitch, customCss];
  return <div css={cssStyles} className={classes}>
      <button id={switchId} aria-checked={checked || false} className="euiSwitch__button" role="switch" type={type} disabled={disabled} onClick={onClick} aria-label={showLabel ? undefined : label} aria-labelledby={showLabel ? labelId : undefined} {...rest}>
        <span className="euiSwitch__body">
          <span className="euiSwitch__thumb" />
          <span className="euiSwitch__track">
            {!compressed && <React.Fragment>
                <EuiIcon type="cross" size="m" className="euiSwitch__icon" />

                <EuiIcon type="check" size="m" className="euiSwitch__icon euiSwitch__icon--checked" />
              </React.Fragment>}
          </span>
        </span>
      </button>

      {showLabel &&
    // <button> + <label> has poor screen reader support.
    // Click handler added to simulate natural, secondary <label> interactivity.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <span {...labelProps} className={labelClasses} id={labelId} onClick={onClick}>
          {label}
        </span>}
    </div>;
};
EuiSwitch.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Whether to render the render the text label
       */
  showLabel: PropTypes.bool,
  /**
       * Must be a string if `showLabel` prop is false
       */
  label: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.string.isRequired]).isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  compressed: PropTypes.bool,
  type: PropTypes.oneOf(["submit", "reset", "button"]),
  /**
       * Object of props passed to the label's <span/>
       */
  labelProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  })
};
try {
  EuiSwitch.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/switch/switch.tsx',
    description: '',
    displayName: 'EuiSwitch',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      showLabel: {
        defaultValue: {
          value: 'true'
        },
        description: 'Whether to render the render the text label',
        name: 'showLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      label: {
        defaultValue: null,
        description: 'Must be a string if `showLabel` prop is false',
        name: 'label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      checked: {
        defaultValue: null,
        description: '',
        name: 'checked',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(event: React.BaseSyntheticEvent<React.MouseEvent<HTMLButtonElement>, HTMLButtonElement, EventTarget & { checked: boolean; }>) => void'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      type: {
        defaultValue: {
          value: 'button'
        },
        description: '',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "reset" | "submit"',
          value: [{
            value: '"button"'
          }, {
            value: '"reset"'
          }, {
            value: '"submit"'
          }]
        }
      },
      labelProps: {
        defaultValue: null,
        description: "Object of props passed to the label's <span/>",
        name: 'labelProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/switch/switch.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLSpanElement>'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'ButtonHTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}